/**
 * NavBar-specific hooks for groups data
 *
 * These hooks are used by the NavBar feature (outside GroupsManagement)
 * to display group navigation.
 *
 * Uses the consolidated groups data source (endpoint with server-computed fields).
 *
 * @module useNavBarGroups
 */

import { useMemo } from "react";

import { useGroupsData } from "./useGroupsData";

/**
 * Basic groups hook - returns groups with query states
 * Used by NavBar and other features outside GroupsManagement
 */
export const useGroups = () => {
    const { groups, isLoading, isError } = useGroupsData();
    return { groups, isLoading, isError };
};

/**
 * Get top-level groups (no parent) for the NavBar
 */
export const useTopGroups = () => {
    const { groups, isLoading, isError } = useGroupsData();

    const topGroups = useMemo(() => {
        return groups.filter(({ parentGroupID }) => !parentGroupID) || [];
    }, [groups]);

    return { isLoading, isError, topGroups };
};

/**
 * Get a mapping of parent group IDs to their child group arrays
 * Used by NavBar for rendering nested groups
 */
export const useParentKeytoChildGroups = () => {
    const { groups, isLoading, isError } = useGroupsData();

    const parentKeyToChildGroups = useMemo(() => {
        const groupsWithParentGroups = groups.filter(
            ({ parentGroupID }) => parentGroupID
        );

        const mapping = {};
        groupsWithParentGroups.forEach((group) => {
            if (!mapping[group.parentGroupID])
                mapping[group.parentGroupID] = [];
            mapping[group.parentGroupID].push(group);
        });

        return mapping;
    }, [groups]);

    return { isLoading, isError, parentKeyToChildGroups };
};

/**
 * Check if the user has any subscribed groups
 * Used to determine if we should show all groups by default when user has no subscriptions
 */
export const useHasSubscribedGroups = () => {
    const { groups, isLoading, isError } = useGroupsData();

    const hasSubscribedGroups = useMemo(() => {
        return groups.some(({ subscribed }) => subscribed);
    }, [groups]);

    return { isLoading, isError, hasSubscribedGroups };
};
